import React, { useState } from 'react';
import { History } from 'lucide-react';
import { useHistoricoDesconto } from '../hooks/useHistoricoDesconto';
import { descreverMudanca, rotuloAlcance, rotuloOrigem, type RegistroDesconto } from '../lib/descontoHistorico';

const dataHora = (iso: string) =>
    new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const LinhaHistorico = ({ r }: { r: RegistroDesconto }) => {
    const soNaFatura = r.escopo === 'fatura';
    const origem = rotuloOrigem(r.origem);
    return (
        <li className={`pl-3 border-l-2 ${soNaFatura ? 'border-amber-400' : 'border-primary'}`}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{descreverMudanca(r)}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${soNaFatura ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-primary/10 text-primary dark:text-primary'}`}>
                    {rotuloAlcance(r)}
                </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {dataHora(r.alterado_em)} · {r.alterado_por_nome || 'Usuário não identificado'}{origem ? ` · pelo ${origem}` : ''}
            </p>
            {r.motivo ? (
                <p className="text-xs text-gray-700 dark:text-gray-300 mt-0.5">“{r.motivo}”</p>
            ) : (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 italic">Sem motivo informado</p>
            )}
        </li>
    );
};

/** Lista do histórico do desconto. Quem carrega os registros é quem usa
 *  (o cálculo da fatura também precisa deles para decidir se pergunta o alcance). */
export const ListaHistoricoDesconto = ({ registros, carregando, erro, limiteInicial = 3 }: {
    registros: RegistroDesconto[];
    carregando: boolean;
    erro: string | null;
    limiteInicial?: number;
}) => {
    const [verTodos, setVerTodos] = useState(false);
    const visiveis = verTodos ? registros : registros.slice(0, limiteInicial);

    return (
        <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
            <div className="flex items-center justify-between mb-3">
                <h5 className="text-[10px] font-bold text-gray-400 uppercase flex items-center gap-1.5">
                    <History size={12} /> Histórico do desconto da UC
                </h5>
                {registros.length > limiteInicial ? (
                    <button type="button" onClick={() => setVerTodos(v => !v)} className="text-xs font-semibold text-primary hover:underline">
                        {verTodos ? 'Ver menos' : `Ver todos (${registros.length})`}
                    </button>
                ) : null}
            </div>
            {erro ? (
                <p className="text-xs text-red-600 dark:text-red-400">Não deu para ler o histórico: {erro}</p>
            ) : carregando && registros.length === 0 ? (
                <p className="text-xs text-gray-400">Carregando…</p>
            ) : registros.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Nenhuma mudança registrada. O histórico começou em 09/10/2026.
                </p>
            ) : (
                <ul className="space-y-3">
                    {visiveis.map(r => <LinhaHistorico key={r.id} r={r} />)}
                </ul>
            )}
        </div>
    );
};

/** Histórico do desconto de uma UC, carregando sozinho (cadastro da UC). */
const HistoricoDescontoUc = ({ ucId }: { ucId: number | string }) => {
    const { registros, carregando, erro } = useHistoricoDesconto(ucId);
    return <ListaHistoricoDesconto registros={registros} carregando={carregando} erro={erro} />;
};

export default HistoricoDescontoUc;
